import { useEffect, useRef, useState } from 'react';
import type { Cell } from '../demo/seed';
import { GRID_SIZE } from '../demo/seed';
import { colorForTier } from '../lib/survival';
import { fmtCellId, fmtTon } from '../lib/format';

type Props = {
  cells: Cell[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  filter: string;
};

export function CellGrid({ cells, selectedId, onSelect, filter }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{ id: number; x: number; y: number } | null>(null);
  const [pixelSize, setPixelSize] = useState(0);

  useEffect(() => {
    function resize() {
      const w = wrapRef.current?.clientWidth ?? 600;
      const h = wrapRef.current?.clientHeight ?? 600;
      const side = Math.min(w, h);
      setPixelSize(side);
    }
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || pixelSize === 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = pixelSize * dpr;
    canvas.height = pixelSize * dpr;
    canvas.style.width = pixelSize + 'px';
    canvas.style.height = pixelSize + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cellPx = pixelSize / GRID_SIZE;
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, pixelSize, pixelSize);

    // grid lines
    ctx.fillStyle = '#111111';
    ctx.fillRect(0, 0, pixelSize, pixelSize);

    // Filter mask
    function showCell(c: Cell): boolean {
      if (filter === 'All') return true;
      if (filter === 'Alive only') return c.agent != null && c.state !== 'dead';
      if (filter === 'Thriving') return c.state === 'thriving';
      if (filter === 'Dying') return c.state === 'critical' || c.state === 'low';
      if (filter === 'Deepest lineages') return (c.agent?.generation ?? 0) >= 5;
      return true;
    }

    for (const cell of cells) {
      const px = cell.x * cellPx;
      const py = cell.y * cellPx;
      const inner = cellPx - 1;

      if (!showCell(cell)) {
        ctx.fillStyle = '#0A0A0A';
        ctx.fillRect(px, py, inner, inner);
        continue;
      }

      let color = colorForTier(cell.state);

      if (cell.state === 'critical') {
        // Flicker at ~4Hz using time-based modulation
        if (Math.floor(performance.now() / 125) % 2 === 0) {
          color = '#1E6FFF';
        }
      }
      ctx.fillStyle = color;
      ctx.fillRect(px, py, inner, inner);

      if (cell.state === 'thriving') {
        const grad = ctx.createRadialGradient(
          px + cellPx / 2,
          py + cellPx / 2,
          0,
          px + cellPx / 2,
          py + cellPx / 2,
          cellPx * 3
        );
        grad.addColorStop(0, 'rgba(0, 255, 136, 0.6)');
        grad.addColorStop(1, 'rgba(0, 255, 136, 0)');
        ctx.globalCompositeOperation = 'lighter';
        ctx.fillStyle = grad;
        ctx.fillRect(px - cellPx * 2, py - cellPx * 2, cellPx * 5, cellPx * 5);
        ctx.globalCompositeOperation = 'source-over';
      }

      if (selectedId === cell.id) {
        ctx.strokeStyle = '#5B9BFF';
        ctx.lineWidth = 2;
        ctx.strokeRect(px - 1, py - 1, inner + 2, inner + 2);
      }
    }
  }, [cells, selectedId, pixelSize, filter]);

  function pixelToCell(clientX: number, clientY: number): Cell | null {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const cellPx = pixelSize / GRID_SIZE;
    const gx = Math.floor(x / cellPx);
    const gy = Math.floor(y / cellPx);
    if (gx < 0 || gy < 0 || gx >= GRID_SIZE || gy >= GRID_SIZE) return null;
    return cells[gy * GRID_SIZE + gx] ?? null;
  }

  return (
    <div ref={wrapRef} className="relative w-full h-full flex items-center justify-center">
      <canvas
        ref={canvasRef}
        onMouseMove={(e) => {
          const c = pixelToCell(e.clientX, e.clientY);
          if (c) setHover({ id: c.id, x: e.clientX, y: e.clientY });
          else setHover(null);
        }}
        onMouseLeave={() => setHover(null)}
        onClick={(e) => {
          const c = pixelToCell(e.clientX, e.clientY);
          if (c) onSelect(c.id);
        }}
        className="cursor-pointer"
      />
      {hover && (
        <div
          className="pointer-events-none fixed z-30 px-2 py-1 hairline bg-panel text-2xs text-text-primary font-mono"
          style={{ left: hover.x + 12, top: hover.y + 12 }}
        >
          {(() => {
            const c = cells[hover.id];
            if (!c) return null;
            return (
              <>
                <div>
                  Cell {fmtCellId(c.id)}
                  {c.agent ? ' · ' + c.agent.name : ''}
                </div>
                {c.agent && (
                  <div className="text-text-secondary">
                    ◊ {fmtTon(c.agent.balance)} TON · Gen {c.agent.generation}
                  </div>
                )}
                {!c.agent && <div className="text-text-mono-dim">empty</div>}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
